import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import EmailCapture from "@/components/EmailCapture";
import { HeroLine, Reveal } from "@/components/Motion";
import { EMAILS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inboxes = [
  { label: "General inquiries", email: EMAILS.general, id: "inbox-general" },
  { label: "Press, podcasts, interviews", email: EMAILS.press, id: "inbox-press" },
  { label: "Book clubs", email: EMAILS.bookclubs, id: "inbox-bookclubs" },
  { label: "Speaking and events", email: EMAILS.events, id: "inbox-events" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("All three fields, please");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Letter received. Thank you.");
      setForm({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Couldn't send. Try again in a moment?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section data-testid="contact-hero-section" className="min-h-[62svh] flex items-end pb-20 md:pb-28" style={{ backgroundColor: "var(--paper)" }}>
        <div className="max-w-[1100px] w-full mx-auto px-6 md:px-12">
          <p className="overline mb-6 animate-overline">Contact</p>
          <h1 data-testid="contact-title" className="font-serif-lit text-5xl sm:text-6xl leading-[1]" style={{ fontWeight: 500 }}>
            <HeroLine delay={0.12}>Get in</HeroLine>
            <HeroLine delay={0.24} className="italic">touch</HeroLine>
          </h1>
          <p className="font-serif-lit text-lg md:text-xl leading-relaxed mt-8 max-w-2xl hero-copy-reveal" style={{ color: "var(--ink-soft)" }}>
            For most things, the best way to reach Rahul is by email.
          </p>
        </div>
      </section>

      <section data-testid="contact-inboxes-section" className="py-20 md:py-28" style={{ backgroundColor: "var(--paper-deep)" }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
            {inboxes.map((inbox, index) => (
              <Reveal as="li" key={inbox.id} testId={inbox.id} className="py-8 border-t" delay={index * 0.06}>
                <p className="overline mb-3" style={{ color: "var(--ink-soft)" }}>{inbox.label}</p>
                <a href={`mailto:${inbox.email}`} data-testid={`${inbox.id}-email-link`} className="font-serif-lit text-xl md:text-2xl literary-link" style={{ color: "var(--ink)" }}>{inbox.email}</a>
              </Reveal>
            ))}
          </ul>
          <Reveal>
            <p className="font-serif-lit italic text-base mt-10 max-w-2xl" style={{ color: "var(--ink-soft)" }}>
              One person reads all four inboxes, so please write to whichever feels most appropriate. We reply within a week.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-form-section" className="py-24 md:py-32" style={{ backgroundColor: "var(--paper)" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <Reveal className="md:col-span-4">
            <p className="overline mb-6">Or write a letter</p>
            <h2 className="font-serif-lit text-3xl md:text-4xl" style={{ fontWeight: 500 }}>Send a note.</h2>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.08}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-9">
              <div>
                <label htmlFor="contact-name" className="overline block mb-2">Your Name</label>
                <input id="contact-name" type="text" required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} data-testid="contact-name-input" className="input-rule" />
              </div>
              <div>
                <label htmlFor="contact-email" className="overline block mb-2">Email</label>
                <input id="contact-email" type="email" required value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} data-testid="contact-email-input" className="input-rule" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="contact-message" className="overline block mb-2">The Message</label>
                <textarea id="contact-message" required rows={5} value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} data-testid="contact-message-input" className="input-rule resize-none" style={{ minHeight: "150px" }} />
              </div>
              <div className="md:col-span-2 pt-2">
                <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-ink">{loading ? "Sending…" : "Send"}</button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <section id="bookclubs" data-testid="bookclubs-section" className="py-24 md:py-32" style={{ backgroundColor: "var(--ink)", color: "var(--paper)" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-8" style={{ color: "var(--warm)" }}>For book clubs</p></Reveal>
          <Reveal delay={0.08}>
            <p className="font-serif-lit text-xl md:text-2xl leading-[1.7] mb-8">
              Rahul is happy to join book club conversations about <em className="italic">Nothing in the Way</em> — in person if local, by video call otherwise. There is no fee. He asks only that the group has read the book before the conversation.
            </p>
            <p className="font-serif-lit text-lg leading-relaxed" style={{ color: "rgba(249,246,240,0.72)" }}>
              To request a book club visit, write to{" "}
              <a href={`mailto:${EMAILS.bookclubs}`} data-testid="bookclub-email-link" className="literary-link" style={{ color: "var(--paper)" }}>{EMAILS.bookclubs}</a>{" "}
              with the date, location (or platform), and roughly how many readers will be there.
            </p>
          </Reveal>
        </div>
      </section>

      <EmailCapture testIdPrefix="contact-newsletter" />
    </>
  );
}